import { z } from 'zod';

/** Modalidades do PNCP (código → nome). */
export const MODALIDADES: Record<number, string> = {
  1: 'Leilão eletrônico',
  2: 'Diálogo competitivo',
  3: 'Concurso',
  4: 'Concorrência eletrônica',
  5: 'Concorrência presencial',
  6: 'Pregão eletrônico',
  7: 'Pregão presencial',
  8: 'Dispensa',
  9: 'Inexigibilidade',
  10: 'Manifestação de interesse',
  11: 'Pré-qualificação',
  12: 'Credenciamento',
  13: 'Leilão presencial',
};

export const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

export const EditaisQuery = z.object({
  q: z.string().trim().max(200).optional(),
  modalidade: z.coerce.number().int().min(1).max(13).optional(),
  uf: z.enum(UFS).optional(),
  /** Só os que passaram no pré-filtro de TI (padrão: sim). */
  ti: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
  /** Só com documento extraído. */
  comTexto: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  publicadoDe: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  publicadoAte: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  valorMinMinor: z.coerce.number().int().nonnegative().optional(),
  valorMaxMinor: z.coerce.number().int().nonnegative().optional(),
  /** Propostas ainda abertas (encerramento no futuro). */
  abertos: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  ordem: z.enum(['publicacao', 'encerramento', 'valor', 'relevancia']).default('publicacao'),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(10).max(100).default(25),
});
export type EditaisQuery = z.infer<typeof EditaisQuery>;

export const EditalResumo = z.object({
  id: z.string(),
  numeroControlePncp: z.string(),
  modalidadeId: z.number(),
  modalidadeNome: z.string(),
  situacaoNome: z.string().nullable(),
  objeto: z.string(),
  orgao: z.object({ cnpj: z.string(), razaoSocial: z.string() }),
  unidadeNome: z.string().nullable(),
  uf: z.string().nullable(),
  municipio: z.string().nullable(),
  valorEstimadoMinor: z.number().nullable(),
  srp: z.boolean(),
  publicadoEm: z.string().nullable(),
  encerramentoPropostasEm: z.string().nullable(),
  preFiltroTi: z.boolean(),
  documentos: z.object({ total: z.number(), extraidos: z.number() }),
  versoes: z.number(),
});
export type EditalResumo = z.infer<typeof EditalResumo>;

export const EditaisResponse = z.object({
  total: z.number(),
  pagina: z.number(),
  porPagina: z.number(),
  itens: z.array(EditalResumo),
});
export type EditaisResponse = z.infer<typeof EditaisResponse>;

export const EditalDocumento = z.object({
  id: z.string(),
  sequencial: z.number(),
  entradaZip: z.string().nullable(),
  tipoNome: z.string().nullable(),
  titulo: z.string().nullable(),
  status: z.enum(['pendente', 'baixado', 'extraido', 'sem_texto', 'ignorado', 'erro']),
  erro: z.string().nullable(),
  tamanhoBytes: z.number().nullable(),
  tipoArquivo: z.string().nullable(),
  paginas: z.number().nullable(),
  paginasSemTexto: z.number().nullable(),
  baixadoEm: z.string().nullable(),
});
export type EditalDocumento = z.infer<typeof EditalDocumento>;

export const EditalDetalhe = EditalResumo.extend({
  numeroCompra: z.string().nullable(),
  processo: z.string().nullable(),
  modoDisputaNome: z.string().nullable(),
  informacaoComplementar: z.string().nullable(),
  valorHomologadoMinor: z.number().nullable(),
  instrumentoNome: z.string().nullable(),
  amparoLegal: z.string().nullable(),
  linkSistemaOrigem: z.string().nullable(),
  linkProcessoEletronico: z.string().nullable(),
  aberturaPropostasEm: z.string().nullable(),
  atualizadoNoPncpEm: z.string().nullable(),
  itens: z.array(
    z.object({
      numero: z.number(),
      descricao: z.string(),
      materialOuServico: z.string().nullable(),
      quantidade: z.string().nullable(),
      unidadeMedida: z.string().nullable(),
      valorUnitarioMinor: z.number().nullable(),
      valorTotalMinor: z.number().nullable(),
    }),
  ),
  documentosLista: z.array(EditalDocumento),
  versoesLista: z.array(z.object({ numero: z.number(), hash: z.string(), detectadaEm: z.string() })),
});
export type EditalDetalhe = z.infer<typeof EditalDetalhe>;

/** Trechos do texto dos documentos que batem com a busca (com a página). */
export const EditalTrechos = z.object({
  trechos: z.array(z.object({ documentoId: z.string(), titulo: z.string().nullable(), pagina: z.number(), trecho: z.string() })),
});
export type EditalTrechos = z.infer<typeof EditalTrechos>;

export const SincronizacaoResponse = z.object({
  fontes: z.array(
    z.object({
      fonte: z.string(),
      chave: z.number(),
      rotulo: z.string(),
      ultimaData: z.string().nullable(),
      ultimaExecucaoEm: z.string().nullable(),
      ultimoSucessoEm: z.string().nullable(),
      ultimoErro: z.string().nullable(),
      registrosUltimaExecucao: z.number().nullable(),
    }),
  ),
  editais: z.object({ total: z.number(), ti: z.number(), hoje: z.number(), ultimos7Dias: z.number() }),
  documentos: z.record(z.string(), z.number()),
  /** Contagem por modalidade de um dia (para bater com o PNCP). */
  porModalidadeOntem: z.array(z.object({ modalidadeId: z.number(), nome: z.string(), total: z.number() })),
});
export type SincronizacaoResponse = z.infer<typeof SincronizacaoResponse>;
